import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { auth, db, secondaryAuth } from "../firebase";
import "./AdminDashboard.css";

function AdminDashboard() {
  const navigate = useNavigate();

  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterStatus, setFilterStatus] = useState("all");
  const [searchText, setSearchText] = useState("");

  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resettingStoreId, setResettingStoreId] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [editingStore, setEditingStore] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [newStore, setNewStore] = useState({
    storeId: "",
    storeName: "",
    ownerName: "",
    email: "",
    password: "",
    phone: "",
    isActive: true,
  });

  useEffect(() => {
    const storesRef = collection(db, "stores");
    const storesQuery = query(storesRef, orderBy("joinedAt", "desc"));

    const unsubscribe = onSnapshot(
      storesQuery,
      (snapshot) => {
        const storeList = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        setStores(storeList);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching stores:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const stats = useMemo(() => {
    const visibleStores = stores.filter((store) => store.isDeleted !== true);

    const totalStores = visibleStores.length;
    const activeStores = visibleStores.filter(
      (store) => store.isActive === true,
    ).length;
    const inactiveStores = visibleStores.filter(
      (store) => store.isActive !== true,
    ).length;
    const deletedStores = stores.filter(
      (store) => store.isDeleted === true,
    ).length;

    return {
      totalStores,
      activeStores,
      inactiveStores,
      deletedStores,
    };
  }, [stores]);

  const filteredStores = useMemo(() => {
    let result = stores.filter((store) => store.isDeleted !== true);

    if (filterStatus === "active") {
      result = result.filter((store) => store.isActive === true);
    }

    if (filterStatus === "inactive") {
      result = result.filter((store) => store.isActive !== true);
    }

    if (filterStatus === "deleted") {
      result = stores.filter((store) => store.isDeleted === true);
    }

    if (searchText.trim()) {
      const search = searchText.toLowerCase();

      result = result.filter((store) => {
        const storeName = store.storeName?.toLowerCase() || "";
        const ownerName = store.ownerName?.toLowerCase() || "";
        const email = store.email?.toLowerCase() || "";
        const storeId = store.storeId?.toLowerCase() || "";

        return (
          storeName.includes(search) ||
          ownerName.includes(search) ||
          email.includes(search) ||
          storeId.includes(search)
        );
      });
    }

    return result;
  }, [stores, filterStatus, searchText]);

  const formatDate = (dateValue) => {
    if (!dateValue) return "Not available";

    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

      return date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Not available";
    }
  };

  const getStoreStatus = (store) => {
    if (store.isDeleted === true) return "Deleted";
    if (store.isActive === true) return "Active";
    return "Suspended";
  };

  const getStoreStatusClass = (store) => {
    if (store.isDeleted === true) return "status-badge inactive";
    if (store.isActive === true) return "status-badge active";
    return "status-badge inactive";
  };

  const goToHome = () => {
    navigate("/", { replace: true });
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/", { replace: true });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setNewStore((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddStore = async (e) => {
    e.preventDefault();

    if (
      !newStore.storeId.trim() ||
      !newStore.storeName.trim() ||
      !newStore.ownerName.trim() ||
      !newStore.email.trim() ||
      !newStore.password.trim()
    ) {
      alert(
        "Store ID, Store Name, Owner Name, Email, and Password are required.",
      );
      return;
    }

    if (newStore.password.trim().length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }

    try {
      setSaving(true);

      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        newStore.email.trim().toLowerCase(),
        newStore.password.trim(),
      );

      const storeUid = userCredential.user.uid;

      await setDoc(doc(db, "stores", storeUid), {
        uid: storeUid,
        storeId: newStore.storeId.trim(),
        storeName: newStore.storeName.trim(),
        ownerName: newStore.ownerName.trim(),
        email: newStore.email.trim().toLowerCase(),
        phone: newStore.phone.trim(),
        isActive: newStore.isActive,
        isDeleted: false,
        joinedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await signOut(secondaryAuth);

      setNewStore({
        storeId: "",
        storeName: "",
        ownerName: "",
        email: "",
        password: "",
        phone: "",
        isActive: true,
      });

      setShowAddForm(false);
      alert("Store account created successfully.");
    } catch (error) {
      console.error("Error creating store:", error);

      if (error.code === "auth/email-already-in-use") {
        alert("This email is already used by another account.");
      } else {
        alert("Something went wrong while creating the store.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStoreStatus = async (storeId, currentStatus) => {
    const confirmMessage = currentStatus
      ? "Are you sure you want to suspend this store?"
      : "Are you sure you want to activate this store?";

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    try {
      const storeRef = doc(db, "stores", storeId);

      await updateDoc(storeRef, {
        isActive: !currentStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating store status:", error);
      alert("Something went wrong while updating store status.");
    }
  };

  const handleViewStore = (store) => {
    setSelectedStore(store);
  };

  const handleOpenEditStore = (store) => {
    setEditingStore({
      id: store.id,
      storeId: store.storeId || "",
      storeName: store.storeName || "",
      ownerName: store.ownerName || "",
      email: store.email || "",
      phone: store.phone || "",
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;

    setEditingStore((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveStoreEdit = async (e) => {
    e.preventDefault();

    if (
      !editingStore.storeId.trim() ||
      !editingStore.storeName.trim() ||
      !editingStore.ownerName.trim()
    ) {
      alert("Store ID, Store Name, and Owner Name are required.");
      return;
    }

    try {
      setEditSaving(true);

      const storeRef = doc(db, "stores", editingStore.id);

      await updateDoc(storeRef, {
        storeId: editingStore.storeId.trim(),
        storeName: editingStore.storeName.trim(),
        ownerName: editingStore.ownerName.trim(),
        phone: editingStore.phone.trim(),
        updatedAt: serverTimestamp(),
      });

      setEditingStore(null);
      alert("Store information updated successfully.");
    } catch (error) {
      console.error("Error updating store:", error);
      alert("Something went wrong while updating the store.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleResetStorePassword = async (store) => {
    const storeEmail = store.email?.trim().toLowerCase();

    if (!storeEmail) {
      alert("This store does not have an email.");
      return;
    }

    const confirmed = window.confirm(
      `Send password reset email to ${storeEmail}?`,
    );

    if (!confirmed) return;

    try {
      setResettingStoreId(store.id);

      await sendPasswordResetEmail(auth, storeEmail);

      alert(`Password reset email sent to ${storeEmail}.`);
    } catch (error) {
      console.error("Password reset error:", error);

      if (error.code === "auth/user-not-found") {
        alert(
          "No Firebase Authentication account exists with this email. Check Authentication > Users.",
        );
      } else if (error.code === "auth/invalid-email") {
        alert("This store email is invalid.");
      } else if (error.code === "auth/too-many-requests") {
        alert("Too many reset attempts. Please try again later.");
      } else {
        alert(
          "Could not send password reset email. Please check Firebase Authentication.",
        );
      }
    } finally {
      setResettingStoreId(null);
    }
  };

  const handleSoftDeleteStore = async (store) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete/suspend ${store.storeName}? This will keep the data but block the store if your store app checks isDeleted/isActive.`,
    );

    if (!confirmed) return;

    try {
      const storeRef = doc(db, "stores", store.id);

      await updateDoc(storeRef, {
        isActive: false,
        isDeleted: true,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert("Store deleted/suspended successfully.");
    } catch (error) {
      console.error("Delete store error:", error);
      alert("Something went wrong while deleting the store.");
    }
  };

  const handleRestoreStore = async (store) => {
    const confirmed = window.confirm(`Restore ${store.storeName}?`);

    if (!confirmed) return;

    try {
      const storeRef = doc(db, "stores", store.id);

      await updateDoc(storeRef, {
        isDeleted: false,
        isActive: true,
        restoredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert("Store restored successfully.");
    } catch (error) {
      console.error("Restore store error:", error);
      alert("Something went wrong while restoring the store.");
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-topbar">
        <div>
          <h1>PointsHub Master Admin</h1>
          <p>Control stores, members, and loyalty activity from one place.</p>
        </div>

        <div className="topbar-actions">
          <button className="home-btn" onClick={goToHome}>
            Home
          </button>
          <button className="home-btn" onClick={() => navigate("/cards")}>
            Card Inventory
          </button>

          <button className="home-btn" onClick={() => navigate("/members")}>
            All Members
          </button>

          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div
          className="stat-card clickable-stat"
          onClick={() => setFilterStatus("all")}
        >
          <p>Total Stores</p>
          <h2>{stats.totalStores}</h2>
        </div>

        <div
          className="stat-card active clickable-stat"
          onClick={() => setFilterStatus("active")}
        >
          <p>Active Stores</p>
          <h2>{stats.activeStores}</h2>
        </div>

        <div
          className="stat-card inactive clickable-stat"
          onClick={() => setFilterStatus("inactive")}
        >
          <p>Suspended Stores</p>
          <h2>{stats.inactiveStores}</h2>
        </div>

        <div
          className="stat-card inactive clickable-stat"
          onClick={() => setFilterStatus("deleted")}
        >
          <p>Deleted Stores</p>
          <h2>{stats.deletedStores}</h2>
        </div>
      </div>

      <div className="dashboard-panel">
        <div className="panel-header">
          <div>
            <h2>Store Management</h2>
            <p>View, search, edit, activate, suspend, or delete stores.</p>
          </div>

          <button
            className="add-store-btn"
            onClick={() => setShowAddForm((prev) => !prev)}
          >
            {showAddForm ? "Close Form" : "+ Add New Store"}
          </button>
        </div>

        {showAddForm && (
          <form className="add-store-form" onSubmit={handleAddStore}>
            <div className="form-row">
              <div className="form-group">
                <label>Store ID</label>
                <input
                  type="text"
                  name="storeId"
                  value={newStore.storeId}
                  onChange={handleInputChange}
                  placeholder="Example: Store_C"
                />
              </div>

              <div className="form-group">
                <label>Store Name</label>
                <input
                  type="text"
                  name="storeName"
                  value={newStore.storeName}
                  onChange={handleInputChange}
                  placeholder="Example: Store C"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Owner Name</label>
                <input
                  type="text"
                  name="ownerName"
                  value={newStore.ownerName}
                  onChange={handleInputChange}
                  placeholder="Owner full name"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={newStore.email}
                  onChange={handleInputChange}
                  placeholder="store@email.com"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Temporary Password</label>
                <input
                  type="password"
                  name="password"
                  value={newStore.password}
                  onChange={handleInputChange}
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={newStore.phone}
                  onChange={handleInputChange}
                  placeholder="4031234567"
                />
              </div>
            </div>

            <div className="form-footer">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newStore.isActive}
                  onChange={(e) =>
                    setNewStore((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                />
                Make store active immediately
              </label>

              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Store"}
              </button>
            </div>
          </form>
        )}

        <div className="filters-row">
          <input
            type="text"
            placeholder="Search by store, owner, email, or store ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Stores</option>
            <option value="active">Active Stores</option>
            <option value="inactive">Suspended Stores</option>
            <option value="deleted">Deleted Stores</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-box">Loading stores...</div>
        ) : filteredStores.length === 0 ? (
          <div className="empty-box">No stores found.</div>
        ) : (
          <div className="table-wrapper">
            <table className="stores-table">
              <thead>
                <tr>
                  <th>Store ID</th>
                  <th>Store Name</th>
                  <th>Owner</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Joined Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredStores.map((store) => (
                  <tr key={store.id}>
                    <td>{store.storeId || "Not available"}</td>
                    <td>{store.storeName || "No name"}</td>
                    <td>{store.ownerName || "Not available"}</td>
                    <td>{store.email || "Not available"}</td>
                    <td>{store.phone || "Not available"}</td>
                    <td>{formatDate(store.joinedAt || store.createdAt)}</td>
                    <td>
                      <span className={getStoreStatusClass(store)}>
                        {getStoreStatus(store)}
                      </span>
                    </td>
                    <td>
                      <div className="store-action-buttons">
                        <button
                          className="view-store-btn"
                          onClick={() => handleViewStore(store)}
                        >
                          View
                        </button>

                        <button
                          className="edit-store-btn"
                          onClick={() => handleOpenEditStore(store)}
                        >
                          Edit
                        </button>

                        <button
                          className="reset-store-btn"
                          onClick={() => handleResetStorePassword(store)}
                          disabled={resettingStoreId === store.id}
                        >
                          {resettingStoreId === store.id
                            ? "Sending..."
                            : "Reset Password"}
                        </button>

                        {store.isDeleted ? (
                          <button
                            className="restore-store-btn"
                            onClick={() => handleRestoreStore(store)}
                          >
                            Restore
                          </button>
                        ) : (
                          <>
                            <button
                              className={
                                store.isActive
                                  ? "status-action deactivate"
                                  : "status-action activate"
                              }
                              onClick={() =>
                                handleToggleStoreStatus(
                                  store.id,
                                  store.isActive === true,
                                )
                              }
                            >
                              {store.isActive ? "Suspend" : "Activate"}
                            </button>

                            <button
                              className="delete-store-btn"
                              onClick={() => handleSoftDeleteStore(store)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedStore && (
        <div className="store-modal-backdrop">
          <div className="store-modal">
            <div className="store-modal-header">
              <div>
                <h2>{selectedStore.storeName}</h2>
                <p>{selectedStore.storeId}</p>
              </div>

              <button onClick={() => setSelectedStore(null)}>×</button>
            </div>

            <div className="store-detail-grid">
              <div className="store-detail-box">
                <p>Owner Name</p>
                <h3>{selectedStore.ownerName || "Not available"}</h3>
              </div>

              <div className="store-detail-box">
                <p>Email</p>
                <h3>{selectedStore.email || "Not available"}</h3>
              </div>

              <div className="store-detail-box">
                <p>Phone</p>
                <h3>{selectedStore.phone || "Not available"}</h3>
              </div>

              <div className="store-detail-box">
                <p>Status</p>
                <h3>{getStoreStatus(selectedStore)}</h3>
              </div>

              <div className="store-detail-box">
                <p>Joined Date</p>
                <h3>
                  {formatDate(
                    selectedStore.joinedAt || selectedStore.createdAt,
                  )}
                </h3>
              </div>

              <div className="store-detail-box">
                <p>Firebase UID</p>
                <h3>{selectedStore.id}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingStore && (
        <div className="store-modal-backdrop">
          <div className="store-modal">
            <div className="store-modal-header">
              <div>
                <h2>Edit Store</h2>
                <p>Update store information</p>
              </div>

              <button onClick={() => setEditingStore(null)}>×</button>
            </div>

            <form className="edit-store-form" onSubmit={handleSaveStoreEdit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Store ID</label>
                  <input
                    type="text"
                    name="storeId"
                    value={editingStore.storeId}
                    onChange={handleEditInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Store Name</label>
                  <input
                    type="text"
                    name="storeName"
                    value={editingStore.storeName}
                    onChange={handleEditInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Owner Name</label>
                  <input
                    type="text"
                    name="ownerName"
                    value={editingStore.ownerName}
                    onChange={handleEditInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={editingStore.email}
                    disabled
                  />
                  <small>
                    Email cannot be changed here because Firebase Auth email
                    also needs to be updated. We can add that later with Cloud
                    Functions.
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={editingStore.phone}
                    onChange={handleEditInputChange}
                  />
                </div>
              </div>

              <div className="edit-store-actions">
                <button
                  type="button"
                  className="cancel-edit-btn"
                  onClick={() => setEditingStore(null)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="save-edit-btn"
                  disabled={editSaving}
                >
                  {editSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
